import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { apiClient } from '../api/client';

interface Player {
  id: string;
  player_id: string;
  player_name: string;
  team: string;
  overall_rating: number;
  assigned_position: string;
}

interface WatchEvent {
  id: string;
  event_type: string;
  team: string;
  player_id: string;
  related_player_id?: string;
  match_time_seconds: number;
  timestamp: string;
  undo_event_id?: string;
}

export function Watch() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [match, setMatch] = useState<any>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [liveData, setLiveData] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedTeam, setSelectedTeam] = useState<'A' | 'B'>('A');
  const [selectedPlayer, setSelectedPlayer] = useState<string>('');
  const [assistPlayer, setAssistPlayer] = useState<string>('');
  const [eventType, setEventType] = useState<'goal' | 'assist'>('goal');

  useEffect(() => {
    if (id) {
      loadMatch();
    }
  }, [id]);

  useEffect(() => {
    if (!id || !liveData?.is_active) return;

    const interval = setInterval(() => {
      refreshLiveData();
    }, 2000);

    return () => clearInterval(interval);
  }, [id, liveData?.is_active]);

  const loadMatch = async () => {
    if (!id) return;

    try {
      setError('');
      setLoading(true);

      const matchResponse = await apiClient.getMatch(id);
      setMatch(matchResponse.match);

      const liveResponse = await apiClient.getLiveMatch(id);
      setLiveData(liveResponse);

      // Get players from match
      const allPlayers: Player[] = [];
      if (matchResponse.match.teams?.team_a) {
        matchResponse.match.teams.team_a.forEach((p: any) => {
          allPlayers.push({
            id: p.id || crypto.randomUUID(),
            player_id: p.player_id,
            player_name: p.name,
            team: 'A',
            overall_rating: p.overall_rating,
            assigned_position: p.assigned_position,
          });
        });
      }
      if (matchResponse.match.teams?.team_b) {
        matchResponse.match.teams.team_b.forEach((p: any) => {
          allPlayers.push({
            id: p.id || crypto.randomUUID(),
            player_id: p.player_id,
            player_name: p.name,
            team: 'B',
            overall_rating: p.overall_rating,
            assigned_position: p.assigned_position,
          });
        });
      }
      setPlayers(allPlayers);

      if (!liveResponse.is_active) {
        const startResponse = await apiClient.startMatch(id);
        setSessionId(startResponse.session_id);
        await refreshLiveData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  const refreshLiveData = async () => {
    if (!id) return;
    try {
      const response = await apiClient.getLiveMatch(id);
      setLiveData(response);
    } catch (err) {
      console.error('Failed to refresh live data:', err);
    }
  };

  const handleRecordGoal = async (team: 'A' | 'B', playerId: string, assistPlayerId?: string) => {
    if (!id) return;

    try {
      setError('');
      await apiClient.recordEvent(id, {
        event_type: 'goal',
        team,
        player_id: playerId,
        related_player_id: assistPlayerId || undefined,
        match_time_seconds: liveData?.elapsed_time_seconds || 0,
      });

      await refreshLiveData();
      setSelectedPlayer('');
      setAssistPlayer('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record goal');
    }
  };

  const handleUndoEvent = async (eventId: string) => {
    try {
      setError('');
      await apiClient.undoEvent(eventId);
      await refreshLiveData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to undo event');
    }
  };

  const handleEndMatch = async () => {
    if (!id || !sessionId) return;

    if (!confirm('End this match?')) return;

    try {
      await apiClient.endMatch(id, sessionId);
      navigate(`/matches/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end match');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getPlayerName = (playerId: string) => {
    const player = players.find(p => p.player_id === playerId);
    return player?.player_name || 'Unknown';
  };

  const activeEvents = liveData?.events?.filter((e: WatchEvent) => !e.undo_event_id) || [];
  const teamAPlayers = players.filter(p => p.team === 'A');
  const teamBPlayers = players.filter(p => p.team === 'B');

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading match...</div>
      </Layout>
    );
  }

  if (!match) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400 mb-4">Match not found</div>
          <Button onClick={() => navigate('/matches')}>Back to History</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">⚽ Live Match</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {formatDate(match.date)} • {match.team_size}v{match.team_size}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="secondary" onClick={() => navigate(`/matches/${id}`)}>
              View Details
            </Button>
            <Button variant="ghost" onClick={handleEndMatch}>
              End Match
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Score Display */}
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex-1 text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">Team A</div>
              <div className="text-6xl font-bold text-gray-900 dark:text-gray-100 my-4">
                {liveData?.team_a_score || 0}
              </div>
            </div>
            <div className="px-8 text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Match Time</div>
              <div className="text-3xl font-mono font-bold text-gray-900 dark:text-gray-100">
                {formatTime(liveData?.elapsed_time_seconds || 0)}
              </div>
            </div>
            <div className="flex-1 text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">Team B</div>
              <div className="text-6xl font-bold text-gray-900 dark:text-gray-100 my-4">
                {liveData?.team_b_score || 0}
              </div>
            </div>
          </div>
        </Card>

        {/* Goal Recording */}
        <Card>
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">⚽ Record Goal</h2>

          <div className="space-y-4">
            {/* Team Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Team
              </label>
              <div className="flex gap-2">
                <Button
                  variant={selectedTeam === 'A' ? 'primary' : 'secondary'}
                  onClick={() => {
                    setSelectedTeam('A');
                    setSelectedPlayer('');
                    setAssistPlayer('');
                  }}
                >
                  Team A
                </Button>
                <Button
                  variant={selectedTeam === 'B' ? 'primary' : 'secondary'}
                  onClick={() => {
                    setSelectedTeam('B');
                    setSelectedPlayer('');
                    setAssistPlayer('');
                  }}
                >
                  Team B
                </Button>
              </div>
            </div>

            {/* Goalscorer Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Goalscorer
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(selectedTeam === 'A' ? teamAPlayers : teamBPlayers).map((player) => (
                  <Button
                    key={player.id}
                    variant={selectedPlayer === player.player_id ? 'primary' : 'secondary'}
                    onClick={() => setSelectedPlayer(player.player_id)}
                    className="justify-start"
                  >
                    {player.player_name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Assist Selection */}
            {selectedPlayer && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Assist (Optional)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={assistPlayer === '' ? 'primary' : 'secondary'}
                    onClick={() => setAssistPlayer('')}
                  >
                    No Assist
                  </Button>
                  {(selectedTeam === 'A' ? teamAPlayers : teamBPlayers)
                    .filter(p => p.player_id !== selectedPlayer)
                    .map((player) => (
                      <Button
                        key={player.id}
                        variant={assistPlayer === player.player_id ? 'primary' : 'secondary'}
                        onClick={() => setAssistPlayer(player.player_id)}
                        className="justify-start"
                      >
                        {player.player_name}
                      </Button>
                    ))}
                </div>
              </div>
            )}

            {/* Submit Button */}
            {selectedPlayer && (
              <Button
                onClick={() => handleRecordGoal(selectedTeam, selectedPlayer, assistPlayer || undefined)}
                className="w-full"
              >
                ⚽ Record Goal
                {assistPlayer && ' + Assist'}
              </Button>
            )}
          </div>
        </Card>

        {/* Events Timeline */}
        <Card>
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">📋 Match Events</h2>
          {activeEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No events yet
            </div>
          ) : (
            <div className="space-y-2">
              {activeEvents.reverse().map((event: WatchEvent) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-mono text-gray-600 dark:text-gray-400">
                      {formatTime(event.match_time_seconds)}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {event.event_type === 'goal' ? '⚽' : '🎯'}{' '}
                        <span className={event.team === 'A' ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}>
                          Team {event.team}
                        </span>
                        {' - '}
                        {getPlayerName(event.player_id)}
                      </div>
                      {event.related_player_id && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Assist: {getPlayerName(event.related_player_id)}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUndoEvent(event.id)}
                  >
                    Undo
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
